import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import Sponsors from "../components/Sponsors";
import Hoofdpartner from "../components/Hoofdpartner";
import CtaBanner from "../components/CtaBanner";
import NewsletterSection from "../components/NewsletterSection";
import { IMAGES, LOCATION, LOGO_WHITE, HERO_VIDEO, EVENT, BEACHVOLLEY } from "../data/site";

const HOME_ACTIVITY_STRANDBAR = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/tw4nw0wa_Strandbar.jpg";
const HOME_ACTIVITY_KINDERSTRAND = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/xh585cji_Kinderstrand.jpg";
const HOME_ACTIVITY_SPORTSTRAND = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/at7nefw4_Sportstrand.jpg";
const HOME_ACTIVITY_VOLLEYBAL = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/jix5ddb6_Volleybal.jpeg";
const HOME_WAT_IS_FOTO = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/mlpo659m_Foto%201.jpg";

const activities = [
  {
    title: "Strandbar",
    text: "Frisse drankjes, zomerse sfeer en gezellige momenten met vrienden, familie of collega's.",
    to: "/strandbar",
    image: HOME_ACTIVITY_STRANDBAR,
  },
  {
    title: "Kinderstrand",
    text: "Een speelse plek waar kinderen kunnen genieten, ontdekken en spelen in het zand.",
    to: "/kinderstrand",
    image: HOME_ACTIVITY_KINDERSTRAND,
  },
  {
    title: "Sportstrand",
    text: "Sportieve activiteiten in een zomerse setting voor wie graag beweegt en plezier maakt.",
    to: "/sportstrand",
    image: HOME_ACTIVITY_SPORTSTRAND,
  },
  {
    title: "Volleybal",
    text: "Zand, sfeer en sportieve competitie tijdens het volleybalprogramma.",
    to: "/volleybal",
    image: HOME_ACTIVITY_VOLLEYBAL,
  },
];

const openingHours = [
  { days: "Maandag – Donderdag", hours: "13:00 – 00:00" },
  { days: "Vrijdag – Zaterdag", hours: "13:00 – 01:00 / 02:00" },
  { days: "Zondag", hours: "13:00 – 00:00" },
];

const HOME_SFEER_VOLLEYKIDS = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/4dmfhg0b_MechelenBeach_VolleyKids-73.jpg";
const HOME_SFEER_BARTENDER = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/wwetonv6_WhatsApp%20Image%202026-06-08%20at%2003.32.11.jpeg";
const HOME_SFEER_TEAM_CAVA = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/ui27jm6i_502692385_695019233283826_6603976957128579495_n.jpg";
const HOME_SFEER_BARMAN_BEERS = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/p3j6b4yn_521673997_732597639525985_1441239269241234948_n.jpg";
const HOME_SFEER_DANCE_OUTDOOR = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/9ktr3apc_523719971_18016534919749830_1543973646888866506_n.jpg";
const HOME_SFEER_DANCE_INDOOR = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/t3981ulj_522862218_18016534928749830_6680808665430924383_n.jpg";
const HOME_SFEER_ROUNDNET = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/vhdhla2c_roundnet.jpg";
const HOME_SFEER_CAPOEIRA = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/ap16ggfh_capoeira-25%20%281%29.jpg";

const HOME_SFEER_BLIK = "https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/6gnw0mqt_Blik%20op%20de%20zomerse%20sfeer.png";

const gallery = [
  HOME_SFEER_VOLLEYKIDS,
  HOME_SFEER_BLIK,
  HOME_SFEER_BARMAN_BEERS,
  HOME_SFEER_DANCE_OUTDOOR,
  HOME_SFEER_BARTENDER,
  HOME_SFEER_TEAM_CAVA,
  HOME_SFEER_DANCE_INDOOR,
  HOME_SFEER_ROUNDNET,
  HOME_SFEER_CAPOEIRA,
];

export default function Home() {
  return (
    <>
      <Seo
        title="Mechelen Beach 2026 | Zomerbar, sport en activiteiten in Mechelen"
        description="Mechelen Beach 2026 — van 16/07/2026 tot 09/08/2026 op het Douaneplein. Strandbar, beachvolleybal, kinderstrand en zomerse sfeer in het hart van Mechelen."
      />

      {/* HERO — full-screen autoplay video, centered white logo + CTAs */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden h-[88svh] min-h-[560px] max-h-[860px] flex items-center justify-center"
      >
        <video
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover"
          src={HERO_VIDEO}
          poster={IMAGES.heroPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        {/* Subtle warm overlay so white text/logo stays readable, atmosphere intact */}
        <div className="absolute inset-0 bg-[#2b1c10]/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2b1c10]/25 via-transparent to-[#2b1c10]/55" />

        <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fadeup">
          <h1 className="sr-only">Mechelen Beach 2026 — Bedankt voor jullie aanwezigheid</h1>
          <img
            data-testid="hero-logo"
            src={LOGO_WHITE}
            alt="Mechelen Beach"
            className="mx-auto h-52 sm:h-64 lg:h-[21rem] w-auto drop-shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
          />

          <p
            data-testid="hero-title"
            className="-mt-4 sm:-mt-6 lg:-mt-8 font-display text-2xl sm:text-3xl lg:text-[2.5rem] font-semibold text-white tracking-tight"
          >
            Mechelen Beach zit er alweer op
          </p>
          <p
            data-testid="hero-subtitle"
            className="mt-4 text-sm sm:text-base lg:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto"
          >
            Bedankt voor jullie aanwezigheid! We kijken terug op een fantastische editie vol sport, sfeer, muziek en zomerse momenten.
          </p>

          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-2.5 sm:gap-3 justify-center items-center">
            <Link
              to="/media/video#aftermovie-2026"
              data-testid="hero-cta-aftermovie"
              className="inline-flex items-center justify-center gap-2 text-white font-semibold text-sm sm:text-base px-5 sm:px-7 py-2.5 sm:py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
              style={{
                background: "linear-gradient(135deg, #ed6a1a 0%, #f8a45c 100%)",
                boxShadow: "0 10px 28px rgba(237, 106, 26, 0.32)",
              }}
            >
              Bekijk de aftermovie
            </Link>
            <Link
              to="/media/fotos"
              data-testid="hero-cta-fotos"
              className="inline-flex items-center justify-center gap-2 text-white font-semibold text-sm sm:text-base px-5 sm:px-7 py-2.5 sm:py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110"
              style={{
                background: "linear-gradient(135deg, #00AFA5 0%, #35D3BE 100%)",
                boxShadow: "0 10px 28px rgba(0, 175, 165, 0.26)",
              }}
            >
              Bekijk de foto's
            </Link>
          </div>
        </div>
      </section>

      <NewsletterSection />

      {/* ABOUT — uses new sfeer photo (terrace with palms) */}
      <section data-testid="home-about" className="bg-[#fdf8ec] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="photo">
              <img
                src={HOME_WAT_IS_FOTO}
                alt="Sfeer op Mechelen Beach — terrasje met palmbomen"
                className="w-full h-[360px] sm:h-[480px] object-cover"
              />
            </div>
            <div>
              <span className="eyebrow">Wat is Mechelen Beach?</span>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05]">
                Wat is Mechelen Beach?
              </h2>
              <p className="mt-6 text-base sm:text-lg text-[#6b5945] leading-relaxed">
                Mechelen Beach brengt het zomergevoel naar het hart van de
                stad. Met zand, strandbar, sport, kinderactiviteiten en
                gezellige sfeer is het een plek waar jong en oud samenkomen om
                te genieten van de zomer in Mechelen.
              </p>
              <Link to="/programma" data-testid="about-cta" className="mt-8 btn-primary">
                Bekijk programma
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WAT VALT ER TE BELEVEN — uses new strandbar photo */}
      <section id="wat-te-beleven" data-testid="home-activities" className="bg-sand-2 py-16 sm:py-24 scroll-mt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="eyebrow">Wat te beleven</span>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05]">
              Wat valt er te <span className="text-orange-gradient">beleven?</span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {activities.map((a, i) => (
              <Link
                key={a.title}
                to={a.to}
                data-testid={`activity-card-${i}`}
                className="group block bg-white rounded-2xl overflow-hidden border border-[#ebd9b5] hover-pop"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={a.image}
                    alt={a.title}
                    className="w-full h-60 object-cover group-hover:scale-[1.05] transition-transform duration-700"
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] bg-orange-gradient text-white shadow-md">
                    {a.title}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-semibold text-[#2b1c10]">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#6b5945] leading-relaxed">
                    {a.text}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#ed6a1a] group-hover:gap-2.5 transition-all">
                    Meer info
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SFEERBEELDEN GALLERY */}
      <section data-testid="home-gallery" className="bg-[#fdf8ec] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="eyebrow">Sfeerbeelden</span>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05]">
              Een blik op de zomerse sfeer
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#6b5945]">
              Een greep uit eerdere edities van Mechelen Beach.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {gallery.map((src, i) => (
              <div
                key={i}
                data-testid={`gallery-${i}`}
                className={`overflow-hidden rounded-xl group ${
                  i === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square"
                }`}
              >
                <img
                  src={src}
                  alt={`Sfeerbeeld ${i + 1}`}
                  className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-700"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPENINGSUREN */}
      <section id="openingsuren" data-testid="home-opening" className="bg-sand-2 py-16 sm:py-24 scroll-mt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <span className="eyebrow">Praktisch</span>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05]">
                Openingsuren
              </h2>
              <p className="mt-5 text-base sm:text-lg text-[#6b5945] leading-relaxed">
                Mechelen Beach 2026 loopt van <span className="font-semibold text-[#2b1c10]">{EVENT.dateRange}</span> op het Douaneplein. Onderstaande openingsuren zijn ter indicatie en worden voor de start van editie 2026 definitief bevestigd.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="bg-white rounded-2xl border border-[#ebd9b5] overflow-hidden">
                {openingHours.map((row, i) => (
                  <div
                    key={row.days}
                    data-testid={`opening-row-${i}`}
                    className={`flex items-center justify-between gap-3 px-4 sm:px-8 py-4 sm:py-5 ${
                      i !== openingHours.length - 1 ? "border-b border-[#ebd9b5]" : ""
                    }`}
                  >
                    <span className="font-semibold text-sm sm:text-base text-[#2b1c10] whitespace-nowrap">
                      {row.days}
                    </span>
                    <span className="font-display text-base sm:text-lg text-orange-gradient font-semibold whitespace-nowrap">
                      {row.hours}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-[#6b5945]">
                * Uren onder voorbehoud — definitieve uren volgen voor de start van editie 2026.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATIE — Google Maps embed */}
      <section data-testid="home-location" className="bg-[#fdf8ec] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="eyebrow">Locatie</span>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2b1c10] leading-[1.05]">
                Waar vind je ons?
              </h2>
              <p className="mt-5 text-base sm:text-lg text-[#6b5945] leading-relaxed">
                Mechelen Beach vindt plaats op het{" "}
                <span className="font-semibold text-[#2b1c10]">Douaneplein / Transit M</span>{" "}
                in Mechelen. Vlot bereikbaar te voet, met de fiets of het openbaar vervoer.
              </p>
              <div className="mt-6 space-y-2 text-sm text-[#2b1c10]">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-[#ed6a1a]" />
                  <span>
                    {LOCATION.name}
                    <br />
                    {LOCATION.postal} {LOCATION.city}, {LOCATION.country}
                  </span>
                </div>
              </div>
              <a
                href={LOCATION.mapsUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="location-cta"
                className="mt-8 btn-primary"
              >
                Open in Google Maps
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#ebd9b5] shadow-[0_24px_50px_-20px_rgba(43,28,16,0.25)]">
              <iframe
                title="Google Maps — Mechelen Beach Douaneplein"
                src={LOCATION.mapsEmbedUrl}
                className="w-full h-[360px] sm:h-[440px] border-0 block"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Klaar voor een zomer in Mechelen?"
        text="Volg Mechelen Beach en blijf op de hoogte van de editie van 2026."
        primaryCta={{ label: "Bekijk programma", to: "/programma" }}
        secondaryCta={{ label: "Contacteer ons", to: "/contact" }}
        image="https://customer-assets.emergentagent.com/job_mechelen-summer/artifacts/lfazejpo_Foto1.jpg"
      />

      {/* SPONSORS — real logo images */}
      <section data-testid="home-sponsors" className="bg-[#fdf8ec] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Partners</span>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-[#2b1c10]">
              Samen maken we Mechelen Beach mogelijk
            </h2>
          </div>
          <div className="mt-10">
            <Hoofdpartner compact />
          </div>
          <div className="mt-10">
            <Sponsors />
          </div>
          <div className="mt-10 text-center">
            <Link to="/partners" data-testid="sponsors-cta" className="btn-ghost">
              Ontdek alle partners
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
